import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth, type TwoFactorSetupChallenge } from './auth-context';

interface LocationState {
  setup?: TwoFactorSetupChallenge;
}

/**
 * Première configuration 2FA pour un compte plateforme (SUPER_ADMIN /
 * ADWEB_OPERATOR). Affichée après /auth/login quand l'API renvoie
 * { requiresSetup: true, ... }. L'utilisateur scanne le QR code dans son
 * authenticator, entre le premier code TOTP, puis est connecté.
 */
export function Setup2FAPage() {
  const { confirmInitial2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const setup = (location.state as LocationState | null)?.setup;

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  if (!setup) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-cream px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg ring-1 ring-slate-200">
          <h1 className="text-xl font-semibold text-slate-900">Session expirée</h1>
          <p className="mt-3 text-sm text-slate-600">
            Relance la connexion pour recommencer la configuration de la double authentification.
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Retour à la connexion
          </Link>
        </div>
      </main>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!setup) return;
    setError(null);
    setBusy(true);
    try {
      await confirmInitial2fa(setup.setupToken, code);
      navigate('/', { replace: true });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Code invalide ou session expirée.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-cream">
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
          <header className="mb-6 text-center">
            <h1 className="text-2xl font-semibold text-slate-900">
              Activer la double authentification
            </h1>
            <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-accent" />
            <p className="mt-3 text-sm text-slate-500">
              Compte&nbsp;: <strong>{setup.email}</strong>
            </p>
          </header>

          <ol className="mb-6 space-y-3 text-sm text-slate-700">
            <li>
              <strong>1.</strong> Ouvre <em>Google Authenticator</em>, <em>Authy</em> ou{' '}
              <em>1Password</em> sur ton téléphone.
            </li>
            <li>
              <strong>2.</strong> Scanne ce QR code (ou clique sur « Saisir manuellement » si
              l'appareil photo ne fonctionne pas).
            </li>
            <li>
              <strong>3.</strong> Entre ci-dessous le code à 6 chiffres affiché par ton application.
            </li>
          </ol>

          <div className="mb-6 flex justify-center">
            <img
              src={setup.qrCodeDataUrl}
              alt="QR code 2FA"
              className="rounded-lg border border-slate-200"
              width={220}
              height={220}
            />
          </div>

          <div className="mb-6 text-center">
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              {showSecret ? 'Masquer' : 'Saisir manuellement le secret'}
            </button>
            {showSecret && (
              <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">
                {setup.secret}
              </div>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="code" className="mb-1 block text-sm font-medium text-slate-700">
                Code à 6 chiffres
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-widest focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </div>
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60"
            >
              {busy ? 'Validation…' : 'Activer et se connecter'}
            </button>
            <Link
              to="/login"
              className="block text-center text-xs text-slate-500 hover:text-slate-700"
            >
              Retour à la connexion
            </Link>
          </form>
        </div>
      </div>
    </main>
  );
}
